// @ts-nocheck
import type { Me } from '@yandex-int/k-common';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b from 'b_';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import { getMe } from 'platform-components/src/components/user/selectors';
import qs from 'query-string';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { usePrevious } from 'react-use';
import ym from 'react-yandex-metrika';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { ReduxTeacherStore } from '../../typings';
import { block } from './constants';

import { CalendarControls } from './__controls/course-calendar__controls';
import { CourseCalendarSchedule } from './__schedule/course-calendar__schedule';
import { actions } from './actions';
import { getCalendarCourses, getCourseCalendarEditor } from './selectors';
import {
    getCurrentWeek,
    getInterval,
    getMetrikaWeek,
    getMonthIntervalFromWeek,
    getMonthIntervalId,
    getPeriodSearchString,
    isValidPeriod,
} from './utils';

interface CourseCalendarProps {
    editorId: string;
    additionalControls?: React.ReactNode | Array<React.ReactNode>;
    mix?: string;
    classId?: string;
    subject?: string;
}

export const CourseCalendar = ({ editorId, additionalControls, mix, classId, subject }: CourseCalendarProps) => {
    const dispatch = useDispatch();
    const params = useParams<{
        classId?: string;
        subject?: string;
    }>();
    const history = useHistory();
    const { search } = useLocation();

    const currentClassId = classId ?? params.classId ?? '';
    const currentSubject = subject ?? params.subject ?? '';
    const { start, end, ...restSearchParams } = qs.parse(search);

    const updateLessonsInfo = useSelector(lessonSelectors.getLessonUpdateInfo);
    const courses = useSelector((store) => getCalendarCourses(store, currentClassId, currentSubject));
    const { currentInterval, markedMonthDays, markedMonthDaysLoadingStatus, columns, intervalLoadingStatus } =
        useSelector((store) => getCourseCalendarEditor(store, editorId));

    const teacher = useSelector<ReduxTeacherStore, Me | null>((store) => getMe(store));
    const teacherId = teacher?.id;

    const prevInterval = usePrevious(currentInterval);
    const prevClassId = usePrevious(currentClassId);
    const prevSubject = usePrevious(currentSubject);
    const prevUpdateLessonsInfo = usePrevious(updateLessonsInfo);
    const isNewInterval = !isEqual(prevInterval, currentInterval);
    const isNewSubject = !isEqual(prevSubject, currentSubject);
    const isNewClassId = !isEqual(prevClassId, currentClassId);
    const isLessonsUpdated = !isEqual(prevUpdateLessonsInfo, updateLessonsInfo);

    useEffect(() => {
        if (isNewSubject || isNewClassId) {
            dispatch(actions.resetEditor({ editorId }));
        }
    }, [dispatch, isNewSubject, isNewClassId, editorId]);

    useEffect(() => {
        if (isValidPeriod(start, end)) {
            dispatch(
                actions.updatePeriod({
                    editorId,
                    interval: getInterval(start, end),
                })
            );
        } else {
            history.replace({ search: getPeriodSearchString(getCurrentWeek(), restSearchParams) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, editorId, history, start, end]);

    useEffect(() => {
        if ((isNewSubject || isNewClassId || isNewInterval || isLessonsUpdated) && currentInterval.length) {
            dispatch(actions.updateIntervalLoadingStatus({ editorId, status: LOADING_STATUSES.LOADING }));
            dispatch(
                actions.fetchPeriodLessons({
                    editorId,
                    interval: currentInterval,
                    courses,
                    classId: currentClassId,
                    subject: currentSubject,
                    // limit: VISIBLE_CARDS_CHUNK,
                })
            );
        }
    }, [
        dispatch,
        currentInterval,
        editorId,
        isNewInterval,
        courses,
        currentClassId,
        currentSubject,
        isNewSubject,
        isNewClassId,
        isLessonsUpdated,
    ]);

    const handleNextClick = useCallback(() => {
        ym('reachGoal', 'teacher_navigation_change_weeek', {
            teacherID: teacherId,
            teamID: currentClassId,
            week: getMetrikaWeek(start, end),
            method: 'right',
        });

        history.replace({ search: getPeriodSearchString({ start, end }, restSearchParams, { weeks: 1 }) });
    }, [history, start, end, restSearchParams, teacherId, currentClassId]);

    const handlePrevClick = useCallback(() => {
        ym('reachGoal', 'teacher_navigation_change_weeek', {
            teacherID: teacherId,
            teamID: currentClassId,
            week: getMetrikaWeek(start, end),
            method: 'left',
        });

        history.replace({ search: getPeriodSearchString({ start, end }, restSearchParams, { weeks: -1 }) });
    }, [history, start, end, restSearchParams, teacherId, currentClassId]);

    const handleChangeWeek = useCallback(
        (startDate: Date, endDate: Date) => {
            ym('reachGoal', 'teacher_navigation_calendar_lessons_choice_weeek', {
                teacherID: teacherId,
                teamID: currentClassId,
                week: getMetrikaWeek(startDate, endDate),
            });

            history.replace({ search: getPeriodSearchString({ start: startDate, end: endDate }, restSearchParams) });
        },
        [history, restSearchParams, teacherId, currentClassId]
    );

    const handleChangeMonth = useCallback(
        (month: Date) => {
            const monthInterval = getMonthIntervalFromWeek(month);
            const monthStart = monthInterval[0].date;
            const monthEnd = monthInterval[monthInterval.length - 1].date;
            const monthId = getMonthIntervalId(monthStart, monthEnd);

            // TODO избавиться от пересечений
            if (!markedMonthDays[monthId]) {
                dispatch(actions.fetchMonthLessons({ editorId, interval: monthInterval, courses }));
            }
        },
        [markedMonthDays, dispatch, editorId, courses]
    );

    const handleShowMore = useCallback(
        (day, maxVisibleCards) => {
            dispatch(actions.updateColumnVisibleCards({ editorId, day, maxVisibleCards }));

            // dispatch(actions.updateColumnLoadingStatus({ editorId, day, status: LOADING_STATUSES.LOADING }));
            // dispatch(
            //     actions.fetchDayLessons({
            //         editorId,
            //         interval: [day],
            //         courses,
            //         classId,
            //         subject,
            //     })
            // );

            ym('reachGoal', 'teacher_navigation_calendar_lessons_open_full', {
                teacherID: teacherId,
                teamID: currentClassId,
                week: getMetrikaWeek(start, end),
            });
        },
        [dispatch, editorId, currentClassId, teacherId, start, end]
    );

    const handleForceCompleteLessonSuccess = useCallback(
        (day) => {
            // const isAllCardsLoaded = columns[day.date]?.isAllCardsLoaded;

            dispatch(actions.updateColumnLoadingStatus({ editorId, day, status: LOADING_STATUSES.LOADING }));
            dispatch(
                actions.fetchDayLessons({
                    editorId,
                    interval: [day],
                    courses,
                    classId: currentClassId,
                    subject: currentSubject,
                    // limit: isAllCardsLoaded ? undefined : VISIBLE_CARDS_CHUNK,
                })
            );
        },
        [dispatch, editorId, courses, currentClassId, currentSubject]
    );

    if (!start || !end) {
        return null;
    }

    return (
        <div className={cx(mix, b(block))}>
            <CalendarControls
                additionalControls={additionalControls}
                interval={currentInterval}
                markedMonthDays={markedMonthDays}
                markedMonthDaysLoadingStatus={markedMonthDaysLoadingStatus}
                onChangeMonth={handleChangeMonth}
                onChangeWeek={handleChangeWeek}
                onNextButtonClick={handleNextClick}
                onPrevButtonClick={handlePrevClick}
            />
            <CourseCalendarSchedule
                classId={currentClassId}
                columns={columns}
                editorId={editorId}
                forceCompleteLessonSuccess={handleForceCompleteLessonSuccess}
                interval={currentInterval}
                intervalLoadingStatus={intervalLoadingStatus}
                onNextButtonClick={handleNextClick}
                onPrevButtonClick={handlePrevClick}
                onShowMoreClick={handleShowMore}
                teacherId={teacherId}
            />
        </div>
    );
};
